import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import { useField } from 'formik';
import { type ComponentProps, memo, type ReactNode } from 'react';

import { Label, type LabelProps } from '@air/primitive-label';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

export type FormikFieldProps = ComponentProps<'div'> & {
  component: ReactNode;
  id: string;
  isLabelHidden?: boolean;
  isErrorHidden?: boolean;
  label: string;
  labelProps?: Omit<LabelProps, 'htmlFor'>;
  name: string;
  description?: string;
};

export const FormikField = memo(
  ({
    className,
    component,
    id,
    isLabelHidden,
    isErrorHidden,
    label,
    labelProps,
    name,
    description,
    ...restOfProps
  }: FormikFieldProps) => {
    const formikId = `formik-field-${id}`;
    const [field, meta] = useField({ name });

    const hasError = !!meta.error && meta.touched;

    const componentProps = { hasError, ...field };

    return (
      <div className={classNames('relative flex flex-col gap-1', className)} {...restOfProps}>
        {label && isLabelHidden ? (
          <VisuallyHidden>
            <Label htmlFor={formikId} size="small">
              {label}
            </Label>
          </VisuallyHidden>
        ) : (
          <Label htmlFor={formikId} size="small" {...labelProps}>
            {label}
          </Label>
        )}

        <Slot id={formikId} {...componentProps}>
          {component}
        </Slot>

        {!hasError && description && (
          <p className="absolute top-full w-full translate-y-0.5 ads:text-metadata text-12 text-gray-10">
            {description}
          </p>
        )}
        {hasError && !isErrorHidden && (
          <div
            className="form-field-error absolute top-full w-full translate-y-0.5 ads:text-error text-12 text-red-9"
            data-fieldid={id}
            data-testid="FORM_ERROR"
          >
            {meta.error}
          </div>
        )}
      </div>
    );
  },
);

FormikField.displayName = 'FormikField';
